import type { QuerySuggestion } from '../api/generated-api';
import type { SidekickChipConfig } from '../components/SidekickSuggestionsCard';

/**
 * Sanitize a raw suggestion text into an id-safe slug for use as a
 * chip id suffix (`ai-sidekick-chip-<id>`). Lowercases, replaces
 * non-alphanumeric runs with `-`, and trims edge dashes so the id
 * stays stable across renders for the same suggestion text.
 *
 * @param text - Raw suggestion text from the memory service.
 * @returns The slugified form, or an empty string when nothing survives.
 */
export const slugifySuggestion = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    // The collapse above leaves at most one dash at each edge, so the
    // trim needs no quantifier. A `-+` here would backtrack on service
    // text made of many separators.
    .replace(/^-|-$/g, '');

const SUGGESTION_HASH_MODULUS = 2_147_483_647;

const buildSuggestionId = (text: string): string => {
  const normalizedText = text.trim().toLowerCase();
  let hash = 0;

  for (const character of normalizedText) {
    hash =
      (hash * 31 + (character.codePointAt(0) ?? 0)) % SUGGESTION_HASH_MODULUS;
  }

  return `suggestion-${slugifySuggestion(normalizedText) || 'query'}-${hash.toString(36)}`;
};

/**
 * Maps raw `memoryServiceGetQuerySuggestions` results to chip configs.
 *
 * Each chip's `label` is the API text verbatim (the service returns
 * prose, not translation keys). Its id is derived from that text so
 * refreshing the same suggestions in a different order preserves chip
 * identity. Repeated text receives an occurrence suffix.
 *
 * Suggestions without usable text are dropped rather than rendered as
 * empty chips.
 *
 * @param suggestions - Raw suggestions from the memory service.
 * @returns Chip configs in the order the service returned them.
 */
export const mapSuggestionsToChips = (
  suggestions: ReadonlyArray<QuerySuggestion> | undefined
): ReadonlyArray<SidekickChipConfig> => {
  const occurrenceById = new Map<string, number>();

  return (suggestions ?? [])
    .filter(
      (s): s is QuerySuggestion & { text: string } =>
        typeof s.text === 'string' && s.text.trim().length > 0
    )
    .map((suggestion) => {
      const baseId = buildSuggestionId(suggestion.text);
      const occurrence = (occurrenceById.get(baseId) ?? 0) + 1;
      occurrenceById.set(baseId, occurrence);

      return {
        id: occurrence === 1 ? baseId : `${baseId}-${occurrence}`,
        label: suggestion.text,
      };
    });
};

/**
 * Builds a `chipId → label` lookup so a click handler can resolve the
 * clicked chip back to the text it should submit without scanning the
 * chip list.
 *
 * @param chips - Chips produced by {@link mapSuggestionsToChips}.
 * @returns A lookup keyed by chip id.
 */
export const buildSuggestionTextById = (
  chips: ReadonlyArray<SidekickChipConfig>
): Record<string, string> =>
  Object.fromEntries(chips.map((chip) => [chip.id, chip.label ?? ''] as const));
